import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore, { setCurrentDate } from 'store/elplan/elplan'
import { Switch } from 'ui/atoms'
import { DatePickerInForm } from 'ui/molecules/pickers/DatePicker/DatePicker'
import Datetime from 'utils/datetime/datetime'
import { useSnapshot } from 'valtio'
import { DayOptions } from './types'

type DatepickerInFormWithSwitchProps = {
  defaultDay: DayOptions
}

export function DatepickerInFormWithSwitch({ defaultDay }: DatepickerInFormWithSwitchProps) {
  const { t } = useTranslation()
  const elplanSnap = useSnapshot(elplanStore)

  const todayDate = Datetime.getTodayDate()
  const tomorrowDate = Datetime.getTomorrowDate()
  const initialDate = defaultDay === 'today' ? todayDate : tomorrowDate

  const selectedStartTime = elplanSnap.date.start_time ?? initialDate.startTime
  const selectedEndTime = elplanSnap.date.end_time ?? initialDate.endTime

  useEffect(() => {
    if (!elplanSnap.date.start_time || !elplanSnap.date.end_time) {
      setCurrentDate(initialDate.startTime, initialDate.endTime)
    }
  }, [elplanSnap.date.start_time, elplanSnap.date.end_time, initialDate.endTime, initialDate.startTime])

  const currentView = useMemo(() => {
    if (selectedStartTime === todayDate.startTime && selectedEndTime === todayDate.endTime) {
      return 'today'
    }
    if (selectedStartTime === tomorrowDate.startTime && selectedEndTime === tomorrowDate.endTime) {
      return 'tomorrow'
    }
    return ''
  }, [
    selectedEndTime,
    selectedStartTime,
    todayDate.endTime,
    todayDate.startTime,
    tomorrowDate.endTime,
    tomorrowDate.startTime,
  ])

  const handleDateChange = (value: ISODateTime): void => {
    const newDate = { startTime: Datetime.getStartOfDay(value), endTime: Datetime.getEndOfDay(value) }
    setCurrentDate(newDate.startTime, newDate.endTime)
  }

  return (
    <>
      <Switch
        value={currentView}
        items={[
          { label: t('Today'), value: 'today' },
          { label: t('Tomorrow'), value: 'tomorrow' },
        ]}
        onClick={(value) => {
          if (value === 'today') {
            handleDateChange(todayDate.startTime)
          } else if (value === 'tomorrow') {
            handleDateChange(tomorrowDate.startTime)
          }
        }}
        setNoTabActive={currentView === ''}
      />
      <DatePickerInForm
        value={selectedStartTime}
        onSubmit={handleDateChange}
        label={t('Chosen date')}
        showWeekNumbers
        granularity={'day'}
      />
    </>
  )
}
